import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface TourStep {
  target: string;
  title: string;
  content: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  disableBeacon?: boolean;
  showProgress?: boolean;
  icon?: string; // Icon name for the step
  action?: string; // Optional action text (e.g., "Click to continue")
}

interface TourContextType {
  isOpen: boolean;
  currentStep: number;
  steps: TourStep[];
  startTour: (steps: TourStep[]) => void;
  nextStep: () => void;
  prevStep: () => void;
  closeTour: () => void;
  skipTour: () => void;
  resetTour: () => void;
  goToStep: (step: number) => void;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export { TourContext };

export function TourProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState<TourStep[]>([]);

  const startTour = (tourSteps: TourStep[]) => {
    setSteps(tourSteps);
    setCurrentStep(0);
    setIsOpen(true);

    // Store that user has seen the tour
    localStorage.setItem('hasSeenTour', 'true');
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      closeTour();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const closeTour = () => {
    setIsOpen(false);
    setCurrentStep(0);
  };

  const skipTour = () => {
    setIsOpen(false);
    setCurrentStep(0);
    // Mark as seen even if skipped
    localStorage.setItem('hasSeenTour', 'true');
  };

  const resetTour = () => {
    // Clear the tour seen flag so it shows again
    localStorage.removeItem('hasSeenTour');
    setIsOpen(false);
    setCurrentStep(0);
    setSteps([]);
  };

  const goToStep = (step: number) => {
    if (step >= 0 && step < steps.length) {
      setCurrentStep(step);
    }
  };

  return (
    <TourContext.Provider
      value={{
        isOpen,
        currentStep,
        steps,
        startTour,
        nextStep,
        prevStep,
        closeTour,
        skipTour,
        resetTour,
        goToStep,
      }}
    >
      {children}
    </TourContext.Provider>
  );
}

export function useTour() {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
}

export function hasUserSeenTour(): boolean {
  return localStorage.getItem('hasSeenTour') === 'true';
}
